import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BookOpen, Copy, ExternalLink, KeyRound, Loader2, Plus, ShieldOff } from 'lucide-react';
import { PERMISSION_LABELS, type Permission, type Role } from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useAccessGroups, useRoles } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { StatusDot } from '../../components/Status';
import { toast } from '../../components/Toasts';
import { EmptyState } from '../../components/EmptyState';
import { relative } from '../../lib/format';
import { copy } from '../../lib/clipboard';

interface ApiKeyDTO {
  id: string; name: string; description: string; prefix: string; role: Role; roleName: string; revokes: Permission[]; permissions: Permission[]; groups: string[];
  expiresAt: string | null; lastUsedAt: string | null; lastUsedIp: string | null; uses: number; revokedAt: string | null;
  createdBy: string | null; createdAt: string; status: 'active' | 'revoked' | 'expired';
}

export function ApiKeysAdmin() {
  const q = useQuery({ queryKey: ['api-keys'], queryFn: () => api<{ items: ApiKeyDTO[] }>('/api/v1/api-keys') });
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<{ key: string; name: string } | null>(null);
  const items = q.data?.items ?? [];

  async function revoke(k: ApiKeyDTO) {
    if (!window.confirm(`Revoke “${k.name}”? Integrations using it stop working immediately.`)) return;
    try {
      await api(`/api/v1/api-keys/${k.id}/revoke`, { method: 'POST', json: {} });
      await qc.invalidateQueries({ queryKey: ['api-keys'] });
      toast(`Revoked ${k.name}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <div className="page" data-testid="api-keys">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <h2>API keys</h2>
          <p className="lede">Keys let other systems use the PRISM integration API: search and read assets, update metadata and timecoded metadata, upload media and attachments, start workflows and fetch EDLs. Each key works with its own role and restrictions, sees only what its access groups allow, and every change it makes is recorded under its name.</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <Link className="btn sm" to="/help/api"><BookOpen size={13} /> API reference</Link>
            <a className="btn sm ghost" href="/api/v1/openapi.json" target="_blank" rel="noreferrer"><ExternalLink size={13} /> OpenAPI JSON</a>
          </div>
        </div>
        <button className="btn primary" onClick={() => setCreating(true)} data-testid="new-api-key"><Plus size={15} /> New API key</button>
      </div>
      {items.length === 0 && !q.isLoading ? (
        <EmptyState style={{ border: '1px dashed var(--border-strong)', borderRadius: 14 }} icon={<KeyRound size={24} />} title="No API keys yet">
          <p>Create one for each system that connects to PRISM, so each can be limited and revoked on its own.</p>
        </EmptyState>
      ) : (
        <table className="table" aria-label="API keys">
          <thead><tr><th>Name</th><th>Key</th><th>Role</th><th>Last used</th><th>Expires</th><th>Status</th><th aria-label="Actions" /></tr></thead>
          <tbody>{items.map((k) => (
            <tr key={k.id} data-testid="api-key-row" style={k.status !== 'active' ? { opacity: 0.6 } : undefined}>
              <td><strong>{k.name}</strong>{k.description && <div className="faint" style={{ fontSize: 12 }}>{k.description}</div>}</td>
              <td className="mono faint">{k.prefix}_…</td>
              <td>{k.roleName}{k.revokes.length > 0 && <div className="faint" style={{ fontSize: 12 }}>without {k.revokes.map((p) => PERMISSION_LABELS[p].label.toLowerCase()).join(', ')}</div>}
                {k.groups.length > 0 && <div className="faint" style={{ fontSize: 12 }}>groups: {k.groups.join(', ')}</div>}</td>
              <td className="muted">{k.lastUsedAt ? `${relative(k.lastUsedAt)}${k.lastUsedIp ? ` · ${k.lastUsedIp}` : ''}` : 'Never'}</td>
              <td className="muted">{k.expiresAt ? relative(k.expiresAt) : 'Never'}</td>
              <td><StatusDot tone={k.status === 'active' ? 'ready' : 'failed'}>{k.status}</StatusDot></td>
              <td style={{ textAlign: 'right' }}>{k.status === 'active' && <button className="btn ghost sm danger" onClick={() => void revoke(k)}><ShieldOff size={13} /> Revoke</button>}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {creating && <CreateKey onClose={() => setCreating(false)} onCreated={(key, name) => { setCreating(false); setIssued({ key, name }); void qc.invalidateQueries({ queryKey: ['api-keys'] }); }} />}
      {issued && <IssuedKey secret={issued.key} name={issued.name} onClose={() => setIssued(null)} />}
    </div>
  );
}

function CreateKey({ onClose, onCreated }: { onClose: () => void; onCreated: (key: string, name: string) => void }) {
  const groups = useAccessGroups().data?.items ?? [];
  const roles = useRoles().data?.items ?? [];
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [revokes, setRevokes] = useState<Permission[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [expires, setExpires] = useState<string>('365');
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      const r = await api<{ key: string }>('/api/v1/api-keys', { method: 'POST', json: { name: name.trim(), description, role, revokes, groupIds, expiresInDays: expires === 'never' ? null : Number(expires) } });
      onCreated(r.key, name.trim());
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }

  return (
    <Modal title="New API key" wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!name.trim() || busy} onClick={() => void create()} data-testid="create-api-key">{busy ? <Loader2 size={14} className="spin" /> : <KeyRound size={14} />} Create key</button>
    </>}>
      <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field"><label htmlFor="key-name">Name</label><input id="key-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Website, Archive sync, Newsroom" /></div>
        <div className="field"><label htmlFor="key-exp">Expires</label>
          <select id="key-exp" className="select" value={expires} onChange={(e) => setExpires(e.target.value)}>
            <option value="30">In 30 days</option><option value="90">In 90 days</option><option value="365">In a year</option><option value="never">Never</option>
          </select>
        </div>
      </div>
      <div className="field"><label htmlFor="key-desc">What uses it</label><input id="key-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
      <div className="field"><label htmlFor="key-role">Acts as</label>
        <select id="key-role" className="select" value={role} onChange={(e) => { setRole(e.target.value as Role); setRevokes([]); }}>
          {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
      </div>
      <div className="field"><label>Allowed (untick to withhold)</label>
        <div className="toggle-grid">
          {(roles.find((r) => r.key === role)?.permissions ?? []).map((p) => (
            <label key={p} className="toggle-row"><input type="checkbox" checked={!revokes.includes(p)} disabled={p === 'admin'} onChange={(e) => setRevokes(e.target.checked ? revokes.filter((x) => x !== p) : [...revokes, p])} />
              <span><strong>{PERMISSION_LABELS[p].label}</strong><span className="help" style={{ display: 'block' }}>{PERMISSION_LABELS[p].help}</span></span></label>
          ))}
        </div>
      </div>
      {groups.length > 0 && (
        <div className="field"><label>Access groups (to see restricted assets)</label>
          <div className="tags">{groups.map((g) => (
            <button type="button" key={g.id} className={`tag toggle ${groupIds.includes(g.id) ? 'on' : ''}`} aria-pressed={groupIds.includes(g.id)}
              onClick={() => setGroupIds(groupIds.includes(g.id) ? groupIds.filter((x) => x !== g.id) : [...groupIds, g.id])}>{g.name}</button>
          ))}</div>
        </div>
      )}
    </Modal>
  );
}

function IssuedKey({ secret, name, onClose }: { secret: string; name: string; onClose: () => void }) {
  const origin = window.location.origin;
  const example = `curl -H "Authorization: Bearer ${secret}" ${origin}/api/v1/assets?q=harbour`;
  return (
    <Modal title={`API key for ${name}`} wide onClose={onClose} actions={<button className="btn primary" onClick={onClose}>Done</button>}>
      <div className="banner warning"><div className="banner-body"><strong>Copy the key now</strong><span>It is shown only once. PRISM keeps just a fingerprint of it; if it is lost, create a new key and revoke this one.</span></div></div>
      <pre className="code-block" data-testid="issued-key">{secret}</pre>
      <button className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => copy(secret)}><Copy size={13} /> Copy key</button>
      <p className="muted" style={{ margin: 0 }}>Send it in the Authorization header. For example:</p>
      <pre className="code-block">{example}</pre>
      <button className="btn sm ghost" style={{ alignSelf: 'flex-start' }} onClick={() => copy(example)}><Copy size={13} /> Copy example</button>
    </Modal>
  );
}
